/**
 * Responsive Image Loading with Fade-in Effect
 * Loads appropriate image sizes for mobile/desktop with smooth fade-in
 */

class ResponsiveImageLoader {
    constructor() {
        this.isMobile = window.innerWidth < 768;
        this.init();
    }

    init() {
        // Handle existing images
        this.handleExistingImages();

        // Set up observer for new images
        this.observeNewImages();

        // Listen for resize events to handle orientation changes
        window.addEventListener(
            "resize",
            this.debounce(() => {
                const wasMobile = this.isMobile;
                this.isMobile = window.innerWidth < 768;

                // If device type changed, reload images with appropriate size
                if (wasMobile !== this.isMobile) {
                    this.handleExistingImages();
                }
            }, 250)
        );
    }

    handleExistingImages() {
        const images = document.querySelectorAll(
            '.product-image-fade-in[data-fade-in="true"]'
        );

        images.forEach((img) => {
            this.setupResponsiveImage(img);
        });
    }

    setupResponsiveImage(img) {
        const mobileSource = img.dataset.mobileSrc;
        const desktopSource = img.dataset.desktopSrc;

        // Debug logging
        console.log("ResponsiveImageLoader Debug:", {
            windowWidth: window.innerWidth,
            isMobile: this.isMobile,
            currentSrc: img.src,
            mobileSource: mobileSource,
            desktopSource: desktopSource,
        });

        if (!mobileSource || !desktopSource) {
            // Fallback: if no responsive sources, just handle normal fade-in
            if (img.complete && img.naturalWidth > 0) {
                this.showImage(img);
            } else {
                this.setupImageLoadHandler(img);
            }
            return;
        }

        // Determine which source to use
        const targetSrc = this.isMobile ? mobileSource : desktopSource;

        console.log("Target source selected:", targetSrc);

        // Normalize URLs for comparison (remove query parameters for comparison)
        const normalizeUrl = (url) => url.split("?")[0];
        const currentNormalized = normalizeUrl(img.src);
        const targetNormalized = normalizeUrl(targetSrc);

        // If the image source is already correct, just handle fade-in
        if (currentNormalized === targetNormalized) {
            console.log("Image source already correct, just showing");
            if (img.complete && img.naturalWidth > 0) {
                this.showImage(img);
            } else {
                this.setupImageLoadHandler(img);
            }
            return;
        }

        // Change to the appropriate source
        console.log("Changing image source from", img.src, "to", targetSrc);
        img.src = targetSrc;
        this.setupImageLoadHandler(img);
    }

    setupImageLoadHandler(img) {
        const handleLoad = () => {
            this.showImage(img);
            img.removeEventListener("load", handleLoad);
            img.removeEventListener("error", handleError);
        };

        const handleError = () => {
            // If image fails to load, still show it
            this.showImage(img);
            img.removeEventListener("load", handleLoad);
            img.removeEventListener("error", handleError);
        };

        img.addEventListener("load", handleLoad);
        img.addEventListener("error", handleError);
    }

    showImage(img) {
        // Add loaded class to trigger fade-in
        img.classList.add("loaded");
    }

    observeNewImages() {
        // Use MutationObserver to handle dynamically added images
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) {
                        // Element node
                        const images = node.querySelectorAll
                            ? node.querySelectorAll(
                                  '.product-image-fade-in[data-fade-in="true"]:not(.loaded)'
                              )
                            : [];

                        // Also check if the node itself is an image
                        if (
                            node.matches &&
                            node.matches(
                                '.product-image-fade-in[data-fade-in="true"]:not(.loaded)'
                            )
                        ) {
                            this.setupResponsiveImage(node);
                        }

                        // Handle images found within the added node
                        images.forEach((img) => {
                            this.setupResponsiveImage(img);
                        });
                    }
                });
            });
        });

        // Start observing
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    // Utility function to debounce resize events
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
        new ResponsiveImageLoader();
    });
} else {
    new ResponsiveImageLoader();
}
